import { useEffect, useState } from 'react';
import type { WinStand } from './WinPunktetafel';
import { useLive } from '../lib/live';
import { useOverlaySeite } from '../lib/overlay';
import type { OverlayState } from '../lib/types';

/**
 * Das Punkte-Overlay für OBS.
 *
 * Unabhängig vom Bingo-Overlay, eigene Adresse, durchsichtiger Hintergrund. Die Ausrichtung kommt
 * aus der Adresse, damit sich dieselbe Quelle wahlweise waagerecht oder senkrecht einbinden lässt.
 */
export function ScoreOverlay({
  overlayKey,
  ausrichtung,
}: {
  overlayKey: string;
  ausrichtung: 'horizontal' | 'vertikal';
}) {
  useOverlaySeite();
  const [win, setWin] = useState<(WinStand & { triggeredAt: string | null; serverNow: string }) | null>(null);
  const [jetzt, setJetzt] = useState(Date.now());
  useEffect(() => {
    let alive = true;
    async function refresh() {
      try {
        const response = await fetch(`/api/overlay/${encodeURIComponent(overlayKey)}/win`);
        if (response.ok && alive) setWin(await response.json() as WinStand & { triggeredAt: string | null; serverNow: string });
      } catch { /* Verbindung unterbrochen: letzten Stand behalten. */ }
    }
    void refresh();
    const refreshTimer = window.setInterval(() => { void refresh(); }, 1000);
    const animationTimer = window.setInterval(() => setJetzt(Date.now()), 100);
    return () => { alive = false; window.clearInterval(refreshTimer); window.clearInterval(animationTimer); };
  }, [overlayKey]);
  const triggerStart = win?.triggeredAt ? Date.parse(win.triggeredAt) : NaN;
  const serverOffset = win?.serverNow
    ? Date.parse(win.serverNow) - jetzt
    : 0;
  const triggerAge = jetzt + serverOffset - triggerStart;
  const listeSichtbar = Number.isFinite(triggerAge) && triggerAge >= 0 && triggerAge < 15000;
  const listeBlendetAus = triggerAge >= 14300;
  const live = useLive<OverlayState>({ overlayKey });

  if (live.fehler) return <div className="overlay-hint">{live.fehler}</div>;
  if (!live.state) return <div className="overlay-hint">verbindet …</div>;

  return (
    <div className="overlay score-overlay">
      <div className={ausrichtung === 'vertikal' ? 'win-scores win-scores-vertical' : 'win-scores'}>
        {(win?.teams ?? live.state.teams.map(team => ({ ...team, points: 0 }))).map(team =>
          <div key={team.id} className="win-score" style={{ backgroundColor: team.color, color: "#ffffff", borderColor: team.color }}>
            <span>{team.name}</span><strong>{team.points}</strong>
          </div>)}
      </div>
      {listeSichtbar && win &&
        <div className={`win-match-list ${listeBlendetAus ? 'win-match-list-out' : ''}`} aria-label="Win-Challenge Matchliste">
          <h2>WIN-CHALLENGE · MATCHES</h2>
          {win.matches.map((match, i) => <div key={match.id} className="win-match-row"
            style={{ backgroundColor: win.teams.find(t => t.id === match.winnerTeamId)?.color ?? undefined }}>
            <span>{i + 1}. {match.title}</span><span>{match.detail}</span><strong>{match.points} Pkt.</strong>
          </div>)}
        </div>}
    </div>
  );
}
